import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Image, Modal, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';

import { AuthContext } from 'components/providers/AuthContextProvider';
import { squidInk } from 'lib/css/colors';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  USERS_GET_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { SUPPORT_TICKET_CREATE } from 'lib/networking/endpoints';
import { SupportTicketOrigins } from 'lib/enums/SupportTicketEnums';
import DropZone from 'components/shared/Dropzone';
import { ProductType } from 'lib/enums/ProductType';
import usePost from 'lib/hooks/usePost';

const SUPPORT_TICKET_FOLDER = 'support_ticket';

function SupportTicketsCreateModal({
  onHide,
  refetch,
  product = null,
  typeOfProduct = null,
  hideDistributorSelect = false,
  presetManufacturer = null,
}) {
  const { authUser } = useContext(AuthContext);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(presetManufacturer);
  const [assignedTo, setAssignedTo] = useState(null);
  const [task, setTask] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const SKU = product?.manufacturer_sku || product?.vendor_sku;

  const subtitleTextParts = [product?.pack_size];
  if (typeOfProduct !== ProductType.GLOBAL && SKU) {
    subtitleTextParts.push(`SKU# ${SKU}`);
  }

  const subtitleText = subtitleTextParts.filter(item => item).join(' | ');

  const { postData: createSupportTicket } = usePost(
    SUPPORT_TICKET_CREATE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Successfully created the ticket',
      });
      refetch();
      onHide();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          `An error occurred while creating the support ticket` +
          ` ${error?.response?.data?.message}`,
      }),
  );

  const handleUploadSuccess = newUrls => {
    setAttachments(prevAttachments => [...prevAttachments, ...newUrls]);
  };

  return (
    <Modal show={true} onHide={onHide} size="xl" className="lato" centered>
      <Modal.Header closeButton>
        <Modal.Title className={classNames(css(styles.heading))}>Create Ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {product && (
          <Row className="mb-2">
            <Col sm={2}>
              <Image src={product?.thumb_url} fluid thumbnail width={120} />
            </Col>
            <Col>
              <div className={css(styles.productName)}>{product?.name}</div>
              <div className="text-muted  mb-2">{subtitleText}</div>
            </Col>
          </Row>
        )}
        {/* Consumers that already know the product's manufacturer (the beta PDP) pass
            `presetManufacturer` and `hideDistributorSelect` so the reporter isn't asked
            to re-pick what we can derive. Every other consumer leaves both unset and
            gets both pickers as before. */}
        <div className={css(styles.section)}>
          <Row>
            {!hideDistributorSelect && (
              <Col>
                <Form.Group>
                  <Form.Label>Select Distributor</Form.Label>
                  <SearchableScrollableSelect
                    initOption={selectedVendor}
                    onChange={option => setSelectedVendor(option)}
                    fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
                    optionMapper={vendor => ({ label: vendor.name, value: vendor.id })}
                    autoFocus={false}
                    disabled={!!selectedManufacturer?.value}
                  />
                </Form.Group>
              </Col>
            )}
            <Col>
              <Form.Group>
                <Form.Label>
                  {presetManufacturer ? 'Manufacturer' : 'Select Manufacturer'}
                </Form.Label>
                {presetManufacturer ? (
                  <Form.Control
                    type="text"
                    value={presetManufacturer.label || ''}
                    readOnly
                    disabled
                  />
                ) : (
                  <SearchableScrollableSelect
                    initOption={selectedManufacturer}
                    onChange={option => setSelectedManufacturer(option)}
                    fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                    optionMapper={manufacturer => ({
                      label: manufacturer.name,
                      value: manufacturer.id,
                    })}
                    autoFocus={false}
                    disabled={!!selectedVendor?.value}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className={css(styles.section)}>
          <Form.Group>
            <Form.Label className="font-weight-bold">Title</Form.Label>
            <Form.Control
              type="text"
              className="mb-3"
              value={task}
              onChange={e => setTask(e.target.value)}
            />
          </Form.Group>
        </div>
        <div className={css(styles.section)}>
          <Form.Group>
            <Form.Label className="font-weight-bold">Describe the problem further</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={description}
              placeholder="Describe the problem further"
              onChange={e => setDescription(e.target.value)}
              className="mb-3"
              style={{ overflowY: 'auto' }}
            />
          </Form.Group>
        </div>
        <div className={css(styles.section)}>
          <Form.Label className="font-weight-bold">Upload optional files</Form.Label>
          <DropZone
            text="Drag and drop the attachments or click to select attachments"
            onUploadSuccess={handleUploadSuccess}
            fileUrls={attachments}
            isPublic={true}
            filePath={SUPPORT_TICKET_FOLDER}
            isDownloadable={true}
          />
        </div>
        <div className={css(styles.section)}>
          <Form.Label className="font-weight-bold">Assign to</Form.Label>
          <SearchableScrollableSelect
            initOption={assignedTo}
            onChange={option => {
              setAssignedTo(option);
            }}
            fetchUrl={USERS_GET_FOR_DROPDOWN}
            optionMapper={user => ({ label: user.name, value: user.id })}
            placeholder="Select user"
            autoFocus={false}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-start">
        {/* With the distributor field suppressed the ticket is anchored by
            fsa_product_id + product_type, which the backend accepts on its own. */}
        <Button
          variant="primary"
          onClick={() =>
            createSupportTicket({
              origin: SupportTicketOrigins.FSA,
              fsa_product_id: product?.id,
              product_type: typeOfProduct,
              global_vendor_id: selectedVendor?.value,
              global_manufacturer_id: selectedManufacturer?.value,
              assigned_to_id: assignedTo?.value,
              reported_by: authUser?.email,
              task: task,
              details: { description: description, attachments: attachments },
            })
          }
          disabled={
            !task ||
            (!hideDistributorSelect && !selectedVendor?.value && !selectedManufacturer?.value)
          }
        >
          Submit
        </Button>
        <div>
          <Button variant="outline-primary" onClick={onHide} className="ml-2">
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  heading: {
    fontWeight: 'bold',
    fontFamily: 'Mont, sans-serif',
    fontSize: '1.5rem',
  },
  section: {
    border: '1px solid #ccc',
    borderRadius: '5px',
    padding: '8px',
    marginBottom: '10px',
  },
  productName: {
    fontSize: 28,
    color: squidInk,
    fontWeight: 800,
  },
});

SupportTicketsCreateModal.propTypes = {
  onHide: PropTypes.func,
  product: PropTypes.object,
  typeOfProduct: PropTypes.string,
  refetch: PropTypes.func,
  hideDistributorSelect: PropTypes.bool,
  presetManufacturer: PropTypes.object,
};

export default SupportTicketsCreateModal;
